import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';
import { agbContent, AgbBlock } from '@/pages/agb/agbContent';

function renderBlock(block: AgbBlock, idx: number) {
  if (block.type === 'list') {
    return (
      <ul key={idx} className="mt-2 ml-4 space-y-1.5 list-none">
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-foreground-600 font-body leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
            <span className="break-words">{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p key={idx} className="text-sm text-foreground-600 font-body leading-relaxed whitespace-pre-line break-words">
      {block.text}
    </p>
  );
}

export default function AgbPage() {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const data = agbContent[lang] ?? agbContent['de'];

  return (
    <>
      <Head
        title={`${t('agb.title')} – Esteva Clinic | Esteva.ch`}
        metas={[
          { name: 'description', content: t('agb.meta_desc') },
          { name: 'keywords', content: t('seo.agb.keywords') },
          { property: 'og:title', content: `${t('agb.title')} – Esteva Clinic` },
          { property: 'og:description', content: t('agb.meta_desc') },
          { property: 'og:url', content: 'https://esteva.ch/agb' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary' },
          { name: 'twitter:title', content: `${t('agb.title')} – Esteva Clinic` },
          { name: 'twitter:description', content: t('agb.meta_desc') },
          { name: 'last-modified', content: '2026-06-11' },
        ]}
        links={[{ rel: 'canonical', href: 'https://esteva.ch/agb' }]}
      />
      <main className="bg-background-50 min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-8 md:pb-12 px-6 md:px-10 bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20elegant%20legal%20document%20texture%20with%20soft%20cream%20and%20ivory%20tones%2C%20sophisticated%20minimalist%20background%20with%20subtle%20paper%20texture%2C%20clean%20legal%20aesthetic%2C%20soft%20warm%20ambient%20lighting%2C%20no%20text%2C%20premium%20professional%20atmosphere&width=1600&height=500&seq=agb-hero-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 to-foreground-950/90"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <p className="text-[10px] tracking-[0.25em] uppercase text-foreground-400 font-label mb-3">
              {t('footer.legal')}
            </p>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white leading-tight">
              {data.title}
            </h1>
            <p className="mt-3 text-sm text-foreground-400 font-body">{data.date}</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 md:py-20 px-4 md:px-10">
          <div className="max-w-3xl mx-auto">

            {/* Subtitle card */}
            <div className="mb-8 p-5 rounded-xl bg-primary-50 border border-primary-100">
              <p className="text-sm text-primary-700 font-body font-medium leading-relaxed">
                {data.subtitle}
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-6">
              {data.sections.map((section) => (
                <div
                  key={section.id}
                  className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <div className="px-6 md:px-8 py-4 bg-foreground-950 flex items-center gap-3">
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/20">
                      <i className="ri-file-text-line text-sm text-primary-300"></i>
                    </div>
                    <h4 id={section.id}>
                      <a href={`#${section.id}`} className="no-underline">
                        <strong className="font-heading text-base md:text-lg text-white font-semibold break-words">
                          {section.title}
                        </strong>
                      </a>
                    </h4>
                  </div>
                  <div className="px-6 md:px-8 py-6 space-y-4">
                    {section.blocks.map((block, idx) => renderBlock(block, idx))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer note */}
            <div className="mt-10 pt-8 border-t border-background-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-foreground-400 font-body text-center sm:text-left break-words">{data.footerText}</p>
              <a
                href="mailto:info@esteva.ch"
                className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-5 py-2.5 min-h-[44px] rounded-full bg-primary-50 text-primary-700 text-xs tracking-[0.1em] uppercase font-label font-semibold hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap no-underline"
              >
                <i className="ri-mail-line"></i>
                info@esteva.ch
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}